<template>
  <v-card
    id="extended-fact-sheet-container"
    flex
    flat
    v-model="itemId"
    :width="width"
  >
    <v-flex
      ml-5
      grow
    >
      <!-- extended HEADER  -->
      <v-card
        flat
      >
        <v-list
          dense
          class="fact-sheet-list"
          flat
        >
          <!-- extended HEADER  -->
          <v-list-tile class="fact-sheet-header extended-header">
            <v-list-tile-content>
              <v-list-tile-title>
                {{ displayItem.label }}
              </v-list-tile-title>
              <v-list-tile-sub-title
                v-if="displayItem.labelAlt"
              >
                {{ displayItem.labelAlt }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <!-- extended CLOSE BUTTON is triggered in FactSheet -->
            <slot name="close-extended"></slot>
          </v-list-tile>
        </v-list>
        <v-divider></v-divider>
      </v-card>

      <!-- extended CONTENT  -->
      <v-card
        flat
        class="extended-content"
        :max-height="extendedHeight"
      >
      <v-flex>
        <v-card
          flat
          max-width="650"
          min-height="20"
        >
          <v-card-text
            v-if="displayItem.description"
          >
            {{ displayItem.description }}
          </v-card-text>
        </v-card>
        <v-card
          class="meta-info"
          flat
          max-width="550"
        >
          <v-list
            v-for="(info, index) in displayItem.metaInfo"
            :key=index
            dense
          >
            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ info.label }}
                </v-list-tile-title>
                <v-list-tile-sub-title
                  id="extended-cutoff"
                  v-if="info.id === 'web'"
                >
                  <a :href="info.url" target="_blank">{{ info.name }}</a>
                </v-list-tile-sub-title>
                <v-list-tile-sub-title
                  v-else-if="info.id === 'loc'"
                  v-html="info.value"
                >
                x
                </v-list-tile-sub-title>
                <v-list-tile-sub-title v-else>
                  {{ info.value }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card>
      </v-flex>
      </v-card>
    </v-flex>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'fact-sheet-extended',
  props: [
    'itemId',
    'width',
    'height'
  ],
  computed: {
    ...mapState([
      'topicMap',
      'geoMap',
      'factSheetItemsHydrated'
    ]),
    ...mapGetters([
      'getCountryFromRegionCode'
    ]),
    extendedHeight () {
      // subtract top padding, header height and paddings
      return this.height - (8 + 50 + 2 * 4)
    },
    displayItem () {
      let item = this.factSheetItemsHydrated.find(i => i.extendedId === this.itemId)

      if (typeof item === 'undefined') {
        // API did not return info for the requested object
        item = {
          title: '',
          description: 'requested data currently not available – we apologise for the inconvenience',
          metaInfo: []
        }
        return item
      } else {
        // check for keys & values; only partial overlap between SIPs & actors
        let obj = {
          label: item.label,
          subtitle: null,
          description: item.description,
          metaInfo: []
        }
        if (typeof item.description !== 'undefined' && item.description === null) {
          // place holder text for SIPs without a description
          obj.description = 'There is no description for this project available.'
        }
        if (item.labelAlt !== 'undefined' && item.labelAlt !== item.label) {
          // actors have no description but a potential English name
          // -> only use for non-English original names
          obj.labelAlt = item.labelAlt
        }
        if (typeof item.website !== 'undefined' && item.website !== null) {
          let website = {
            id: 'web',
            label: 'Website',
            url: item.website,
            name: item.website
          }
          obj.metaInfo.push(website)
        }
        if (typeof item.location !== 'undefined' && item.location.length > 0) {
          let location = {
            id: 'loc',
            label: 'Location',
            value: null
          }
          let locsFormatted = []
          let locs = item.location
          locs.forEach(l => {
            // actors use city, country
            if (typeof l.city !== 'undefined') {
              location.label = 'Headquarters'
              locsFormatted.push(l.city + ', ' + l.country_name)
            }
            // SIP use region code, country code
            if (typeof l.region !== 'undefined') {
              // get country + region names from IDs
              let r = this.geoMap.get(l.region)
              let c = this.geoMap.get(l.country)
              locsFormatted.push(r + ', ' + c)
            }
          })
          location.value = locsFormatted.join(',<br>')
          obj.metaInfo.push(location)
        }
        if (typeof item.topic !== 'undefined' && item.topic.length > 0) {
          let topics = {
            id: 'top',
            label: 'Topics',
            value: null
          }
          let topicNames = item.topic.map(t => this.topicMap.get(t).text)
          topicNames.sort(function (a, b) {
            return a.localeCompare(b, 'en', {sensitivity: 'base'})
          })
          topics.value = topicNames.join(', ')
          obj.metaInfo.push(topics)
        }
        // display start and and year only if they exist (most SIPs don't included these values)
        // TODO consider nicer way of displaying project duration if both start + end year are available
        if (item.start_year) {
          obj.metaInfo.push({
            id: 'init',
            label: 'Project start',
            value: item.start_year // check if this is a number
          })
        }
        if (item.end_year) {
          obj.metaInfo.push({
            id: 'fin',
            label: 'Project end',
            value: item.end_year // check if this is a number
          })
        }
        if (item.foundation_year) { // TODO have changed to founding_year or founded
          obj.metaInfo.push({
            id: 'est',
            label: 'Founded',
            value: item.foundation_year
          })
        }

        return obj
      }
    }
  }
}
</script>

<style scoped lang="css">
  >>> .extended-content {
    overflow-y: auto;
  }

  >>> .extended-header .v-list__tile {
    height: 50px;
  }
  .extended-header .v-list__tile__title {
    font-size: 17px;
  }
  .extended-header .v-list__tile__sub-title {
    font-weight: normal;
    font-size: 14px;
  }
  .extended-header .v-list__tile__content {
    justify-content: flex-end;
    padding-bottom: 4px;
  }

  >>> .meta-info {
    padding-bottom: 20px;
  }
  >>> .meta-info .v-list__tile {
    height: unset;
    min-height: 30px;
  }
  .meta-info .v-list__tile__content {
    flex-direction: row;
    flex: 0 1 auto;
  }
  .meta-info .v-list__tile__title,
  .meta-info .v-list__tile__sub-title {
    font-size: 14px;
  }
  .meta-info .v-list__tile__title {
    min-width: 120px;
    width: unset;
  }
  .meta-info .v-list__tile__sub-title {
    overflow: unset;
    text-overflow: unset;
    white-space: normal;
  }

  /* for elements which need not get displayed fully, e.g. website urls */
  #extended-cutoff {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>



// WEBPACK FOOTER //
// src/components/FactSheet_Extended.vue